import { Product, ProductIdentifiers, ProductInfoComponent } from '@inter-ikea-storageone/range';
import { ComponentIds, HierarchyUtils, ProductPartComponent } from '@inter-ikea-core/core';
import { Entity } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import { PartKey, ProductType } from '@inter-ikea-storageone/range';
import { isPullOutInterior } from './selectedMenuProductFilters';
import { sortCornerSolutionsBySize } from '../sorting/sort';

type Dimensions = { height: number; width: number; depth: number };

// Private helper Functions \\
// ---------------------------- \\

const getDimensionsFromEntity = (entity: Entity): Dimensions => {
  const size = entity.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  if (!size) {
    throw new Error(`Entity is missing commmunication size: ${entity}`);
  }
  return size;
};

const getDimensionsFromEntities = (entities: Entity[]) =>
  entities.map(entity => getDimensionsFromEntity(entity));

const getIsPartOfCornerSolution = (entity: Entity) =>
  !!HierarchyUtils.findWithComponentInUpwardsHierarchy(entity, CornerSolutionComponent.id);

const getCornerSolutionFrames = (
  entity: Entity
): { regularFrameWidth: number; cornerFrameDepth: number } => {
  const regularFrame = HierarchyUtils.hierarchy(entity).find(
    f =>
      f.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)?.partKey ===
      PartKey.CORNER_SOLUTION_NORMAL_FRAME
  );
  const cornerFrame = HierarchyUtils.hierarchy(entity).find(
    f =>
      f.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)?.partKey ===
      PartKey.CORNER_SOLUTION_CORNER_FRAME
  );

  return {
    regularFrameWidth: regularFrame
      ? regularFrame.getComponent<CommunicationSizeComponent>(
          ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
        )?.size.width || 0
      : 0,
    cornerFrameDepth: cornerFrame
      ? cornerFrame.getComponent<CommunicationSizeComponent>(
          ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
        )?.size.depth || 0
      : 0
  };
};

const productFitsCornerSolution = (
  productDimensions: Dimensions,
  frameDimensions: Dimensions,
  frame: Entity
) => {
  const cornerSolutionDimensions = getCornerSolutionFrames(frame);

  if (cornerSolutionDimensions) {
    // These cases all have matching doors
    if (
      (cornerSolutionDimensions.cornerFrameDepth === 580 &&
        cornerSolutionDimensions.regularFrameWidth === 750) ||
      (cornerSolutionDimensions.cornerFrameDepth === 350 &&
        cornerSolutionDimensions.regularFrameWidth === 500)
    ) {
      return true;
    }

    // If the above check fails this check ensures doors with the width of 250 or 375 is filtered from the product list as no other corner solution combination supports those doors
    if (productDimensions.width === 250 || productDimensions.width === 375) {
      return false;
    }
    return (
      productDimensions.height === frameDimensions.height &&
      productDimensions.width <= frameDimensions.width
    );
  }
};

const doorProductMatchesHeightAndWidthLowerThanOrEqualToFrameDimensions = (
  productDimensions: Dimensions,
  frames: Entity[]
) => {
  return frames.some(frame => {
    const frameDimensions = getDimensionsFromEntity(frame);
    const frameIsPartOfCornerSolution = getIsPartOfCornerSolution(frame);

    if (frameIsPartOfCornerSolution) {
      return productFitsCornerSolution(productDimensions, frameDimensions, frame);
    }
    // Only show 250 wide doors for 500 and 750 wide frames
    if (
      (productDimensions.width === 250 && frameDimensions.width === 500) ||
      (productDimensions.width === 250 && frameDimensions.width === 750)
    ) {
      return productDimensions.height === frameDimensions.height;
    }
    // Ensures we get right frame for the 375 door
    if (productDimensions.width === 375) {
      return frameDimensions.width === 750 && productDimensions.height === frameDimensions.height;
    }
    // Ensures we get no 250 doors if we only have a 1000 wide frame in the scene
    if (productDimensions.width === 250 && frameDimensions.width === 1000) {
      return false;
    }

    // If no other check falls through then we filter the doors by width and height
    return (
      productDimensions.height === frameDimensions.height &&
      productDimensions.width <= frameDimensions.width
    );
  });
};

const matchHeight = (productDimensions: Dimensions, entityDimensions: Dimensions[]) =>
  entityDimensions.some(e => productDimensions.height === e.height);

export const matchSlidingDoorsWidth = (
  productDimensions: Dimensions,
  entityDimensions: Dimensions[]
) => {
  const nuberOf50FramesInScene = entityDimensions.filter(entity => entity.width === 500).length;
  const nuberOf75FramesInScene = entityDimensions.filter(entity => entity.width === 750).length;
  const nuberOf100FramesInScene = entityDimensions.filter(entity => entity.width === 1000).length;
  if (
    (productDimensions.width === 1500 && nuberOf75FramesInScene >= 2) ||
    (productDimensions.width === 2000 &&
      (nuberOf50FramesInScene >= 4 || nuberOf100FramesInScene >= 2)) ||
    (productDimensions.width === 2000 &&
      nuberOf50FramesInScene >= 2 &&
      nuberOf100FramesInScene >= 1)
  ) {
    return true;
  }
  return false;
};

const interiorsFitsCornerSolution = (
  productDimensions: Dimensions,
  frameDimensions: Dimensions,
  product: Product
) => {
  // Interiors in size 50x35 fits in all corner solutions
  if (
    productDimensions.depth === 350 &&
    (productDimensions.width === 500 || product.type === ProductType.PAX_PULL_OUT_MULTI_USE_HANGER)
  ) {
    return true;
  }

  // Ensures that we don't get pull out interiors for the wide frame in a corner solution
  // They can only be added to the add-on frame
  if (frameDimensions.width >= 500 && !isPullOutInterior(product)) {
    return (
      productDimensions.width === frameDimensions.width &&
      productDimensions.depth === frameDimensions.depth
    );
  }
  return false;
};

const productMatchesInexactWidthAndHeight = (product: Product, frames: Entity[]) => {
  const productDimensions = product.communicatedDimensions;

  return frames.some(f => {
    const frameDimensions = getDimensionsFromEntity(f);
    const frameIsPartOfCornerSolution = getIsPartOfCornerSolution(f);

    if (
      product.type === ProductType.PAX_VALET_HANGER ||
      (product.type === ProductType.PAX_SHOE_SHELF &&
        productDimensions.width === frameDimensions.width) ||
      (product.type === ProductType.PAX_PULL_OUT_MULTI_USE_HANGER &&
        productDimensions.depth === frameDimensions.depth)
    ) {
      return true;
    }

    if (frameIsPartOfCornerSolution) {
      return interiorsFitsCornerSolution(productDimensions, frameDimensions, product);
    }
    return (
      productDimensions.height <= frameDimensions.height &&
      productDimensions.width === frameDimensions.width &&
      productDimensions.depth === frameDimensions.depth
    );
  });
};

const productFitsInDividers = (product: Product, dividers: Entity[]) => {
  const productDimensions = product.communicatedDimensions;

  return dividers.some(d => {
    const dividerDimensions = getDimensionsFromEntity(d);
    return product.type === ProductType.PAX_SHOE_SHELF
      ? productDimensions.width === 500
      : productDimensions.width === 500 && productDimensions.depth === dividerDimensions.depth;
  });
};

const dividerFitsInScene = (product: Product, entities: Entity[]) => {
  const productDimensions = product.communicatedDimensions;
  return entities.some(e => {
    const entityIsPartOfCornerSolution = getIsPartOfCornerSolution(e);
    const entityDimensions = getDimensionsFromEntity(e);

    if (entityIsPartOfCornerSolution && product.type === ProductType.PAX_T_DIVIDER) {
      return false;
    }

    return (
      entityDimensions.width >= 750 &&
      productDimensions.depth === entityDimensions.depth &&
      entityDimensions.width >= productDimensions.width
    );
  });
};

const cleaningInteriorFitsInScene = (frames: Entity[]) => {
  return frames.some(e => getDimensionsFromEntity(e).depth === 580);
};

const getFramesFromEntitiesInScene = (entitiesInScene: Entity[]) => {
  // Sometimes we have assemblies that have hierarchies of frames
  // In those cases we need to find the frame component from the hierarchy tree instead of using the main assembly entity
  return entitiesInScene
    .flatMap(entity => {
      const entityHierarchy = HierarchyUtils.hierarchy(entity);
      return entityHierarchy.filter(e => e.hasComponent(ComponentIds.FRAME_COMPONENT_ID));
    })
    .filter(Boolean);
};

const getDividersFromEntitiesInScene = (entitiesInScene: Entity[]) =>
  entitiesInScene.filter(
    e =>
      e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
        ProductType.PAX_L_DIVIDER ||
      e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
        ProductType.PAX_T_DIVIDER
  );

const isSlidingDoor = (productType: string) =>
  productType === ProductType.PAX_SLIDING_DOOR_REGULAR_ASM ||
  productType === ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM;

// Private helper functions end \\
// -------------------------------- \\

// Public functions \\
// ---------------------- \\
// Filter doors to frames in scene \\
export const matchDoorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = getFramesFromEntitiesInScene(entitiesInScene);
  const frameDimensions = getDimensionsFromEntities(frames);

  return frames.length === 0
    ? products
    : products.filter(product => {
        if (isSlidingDoor(product.type)) {
          return (
            matchSlidingDoorsWidth(product.communicatedDimensions, frameDimensions) &&
            matchHeight(product.communicatedDimensions, frameDimensions)
          );
        }
        return doorProductMatchesHeightAndWidthLowerThanOrEqualToFrameDimensions(
          product.communicatedDimensions,
          frames
        );
      });
};

// Only show one of the corner solutions in the list (the one that can have 50cm wide doors on both frames)
export const filterCornerSolutions = (products: Product[]) => {
  const regularFrames = products.filter(p => p.type !== ProductType.PAX_CORNER_SOLUTION_ASM);
  const regularCornerSolutions = products.filter(
    p =>
      p.type === ProductType.PAX_CORNER_SOLUTION_ASM &&
      !p.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT)
  );
  const depth = regularFrames[0]?.communicatedDimensions.depth;
  const sortedRegularCornerSolutions = sortCornerSolutionsBySize(regularCornerSolutions, depth);
  return [...regularFrames, sortedRegularCornerSolutions[0]];
};

// Filter interiors to frames in scene
export const matchInteriorsToFrames = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = getFramesFromEntitiesInScene(entitiesInScene);
  const dividers = getDividersFromEntitiesInScene(entitiesInScene);
  if (frames.length === 0) {
    return products;
  }

  return products
    .map(product => {
      if (
        product.type === ProductType.PAX_T_DIVIDER ||
        product.type === ProductType.PAX_L_DIVIDER
      ) {
        return dividerFitsInScene(product, frames) ? product : null;
      }
      if (product.type === ProductType.PAX_CLEANING_INTERIOR) {
        return cleaningInteriorFitsInScene(frames) ? product : null;
      }

      return productMatchesInexactWidthAndHeight(product, frames) ||
        productFitsInDividers(product, dividers)
        ? product
        : null;
    })
    .filter((p): p is Product => p !== null);
};

export const matchDesign = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = getFramesFromEntitiesInScene(entitiesInScene);
  if (frames.length === 0) {
    return products;
  }
  const design = frames[0].getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design;
  return products.filter(p => p.design === design);
};

const isOrganizer = (type: string) => {
  return (
    type === ProductType.PAX_SKUBB_STORAGE_CASES ||
    type === ProductType.PAX_SKUBB_SHOE_BOXES ||
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE ||
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH ||
    type === ProductType.PAX_SKUBB_STORAGE_BOXES ||
    type === ProductType.PAX_HEMMAFIXARE_SHOE_BOX ||
    type === ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP ||
    type === ProductType.PAX_SKUBB_STORAGE_CASE_WRAP ||
    type === ProductType.PAX_SHOE_MAT
  );
};

const accessoryFitsInScene = (product: Product, entities: Entity[]) => {
  const productDimensions = product.communicatedDimensions;
  return entities.some(e => {
    const entityDimensions = getDimensionsFromEntity(e);

    if (
      [
        ProductType.PAX_SKUBB_STORAGE_CASES,
        ProductType.PAX_SKUBB_SHOE_BOXES,
        ProductType.PAX_SKUBB_STORAGE_BOXES
      ].includes(product.type)
    ) {
      return (
        productDimensions.width <= entityDimensions.width &&
        productDimensions.depth <= entityDimensions.depth
      );
    } else if (
      [
        ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
        ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
      ].includes(product.type)
    ) {
      return (
        (entityDimensions.width === 500 || entityDimensions.width === 1000) &&
        entityDimensions.depth === 350
      );
    } else if ([ProductType.PAX_SHOE_MAT].includes(product.type)) {
      return entityDimensions.depth === 350;
    } else {
      if (entityDimensions.width === 500 && entityDimensions.depth === 350) {
        return null;
      } else if (entityDimensions.width > 500 && entityDimensions.depth === 350) {
        return productDimensions.width === 340;
      } else {
        return productDimensions.width <= entityDimensions.width;
      }
    }
  });
};

export const matchAccesoriesToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = getFramesFromEntitiesInScene(entitiesInScene);

  return frames.length === 0
    ? products
    : products.filter(product => {
        if (product.type === ProductType.PAX_SKUBB_HANGING_STORAGE) {
          return entitiesInScene.find(
            e =>
              e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
              ProductType.PAX_CLOTH_RAIL_580
          );
        } else if (isOrganizer(product.type)) {
          return accessoryFitsInScene(product, frames);
        } else {
          return product;
        }
      });
};

export const matchLightsToFramesInScene = (products: Product[], entitiesInScene: Entity[]) => {
  const frames = getFramesFromEntitiesInScene(entitiesInScene);
  const frameWidths = getDimensionsFromEntities(frames).map(d => d.width);
  const dividersInScene = getDividersFromEntitiesInScene(entitiesInScene);

  return frames.length === 0
    ? products
    : products.filter(product => {
        if (product.type === ProductType.PAX_OUTER_LIGHT) {
          return true;
        }

        if (product.communicatedDimensions.width === 960) {
          return frameWidths.includes(1000);
        } else if (product.communicatedDimensions.width === 710) {
          return frameWidths.includes(750);
        } else if (product.communicatedDimensions.width === 460) {
          return frameWidths.includes(500) || dividersInScene.length > 0;
        }
      });
};
